import i18n from '@/plugins/i18n'
import { getI18n } from '@/plugins/i18n'
import moment from 'moment'
import * as dataGridColumn from '@/composables/data-grid-column'

import { useLookup, useDefaultDialogTitle, useCurrencyColumnFormatter } from '@/composables/data-grid'
import { useSubjectService } from '@/services/subject.service'
import { useDocumentService } from '@/services/document.service'
import { useValidation } from '@/composables/validation'

import { useGarageStore } from '@/stores/garage.store'
import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useSaleStore } from '@/stores/sale.store'

export const useDialogTitle = () => {
  const { t } = i18n.global
  const { defaultActionTitle } = useDefaultDialogTitle()
  const portalStore = usePortalStore()
  const registryStore = useRegistryStore()

  const itemTitle = (i) => {
    const customer = registryStore.customers.find(c => c.id === i.customer_id) || null
    return i ? `${i.number_mnemonic}/${i.series} ${customer ? customer?.name : ''}` : ''
  }

  const dialogTitle = (i) => {
    const documentType = i.sale_document_type != null ? portalStore.getDocumentTypeName(i.sale_document_type) : ''
    const title = `${defaultActionTitle(i)} ${t('common.document')} ${documentType} `
    return i.id ? `${title}- ${itemTitle(i)}` : title
  }

  return { dialogTitle, itemTitle }
}

export const useColumns = (
  editors,
  gridRef = {},
  currentCustomer = {},
  currentSaleDocumentType = {},
  currentCountryId = {},
  currentStateId = {},
  currentDeliveryCountryId = {},
  currentDeliveryStateId = {},
  currentCustomerTypeId = {},
  currentCustomerSubTypeId = {},
  currentCarService = {},
  currentSaleDocumentPage = {},
  isBillingCustomerDisabled = {},
  currentVehicleFuelType = {},
  updateTabPanelVisibility,
  formRepaintTabHandler,
  getEditColumns,
  includesVat = {},
  isPaid = {},
  currentCustomerVatCountryId = {},
  currentCustomerVatCountryCode = {},
  setCellValueZipCode,
  setCellValueCity
) => {
  const i18n = getI18n()
  const portalStore = usePortalStore()
  const garageStore = useGarageStore()
  const webSiteStore = useWebSiteStore()
  const registryStore = useRegistryStore()
  const saleStore = useSaleStore()
  const { setSdiDocumentTypeValue, setIncludesVat } = useDocumentService()

  const { withDocumentSeriesAndNumberMnemonicUpdate: _withUpdate } = dataGridColumn.useDocumentSeriesAndNumberMnemonicUpdate()
  const trackedDocumentIdFields = ['sale_document_type']
  const withDocumentSeriesAndNumberMnemonicUpdate = (fn) =>
    _withUpdate(fn, trackedDocumentIdFields, editors, saleStore.api.getSaleDocumentNextIdByDataAsync)

  const { setSubjectType, isVatCountryIta, isNameExtMandatory } = useSubjectService()
  const { validateTaxCode } = useValidation()

  const getSubjectTypes = (options) => {
    return {
      store: registryStore.getLocalizedSubjectTypes(),
      filter: ['parent_id', '=', null],
      sort: 'name',
    }
  }

  const getSubjectSubTypes = (option) => {
    return {
      store: registryStore.getLocalizedSubjectTypes(),
      filter: ['parent_id', '=', option.data?.customer?.subject_type_ref || 0],
      sort: 'name',
    }
  }

  const validatePlateNumberNoSpaces = (e) => {
    if (registryStore.companyData.vehicle_plate_allow_blank_spaces) {
      return true
    }

    const value = e.value || ''
    return !/\s/.test(value)
  }

  const setReadOnlyDeliveryAddress = (currentData) => {
    const fields = ['delivery_address.name', 'delivery_address.email', 'delivery_address.mobile', 'delivery_address.phone', 'delivery_address.address.zip_code', 'delivery_address.address.street']

    fields.forEach(field => {
      const isReadOnly = !currentData.customer_id || !!currentData.delivery_address_ref
      editors[field].option('readOnly', isReadOnly)
    })
  }

  const hasAnyValue = (obj) => {
    return Object.entries(obj || {}).some(([key, value]) => {
      if (key === 'id') return false

      if (value && typeof value === 'object') {
        return hasAnyValue(value)
      }

      return value !== null && value !== undefined && value !== ''
    })
  }

  return [
    {
      type: 'buttons',
      buttons: [{template: 'editButtonsTemplate'}],
      width: '4rem',
      visibleIndex: 0,
      fixed: false
    },
    dataGridColumn.useAttachmentColumn(),
    dataGridColumn.useDocumentProgressStatusColumn({
      caption: i18n.t('common.documentProgressStatus'),
      dataField: 'document_progress_status',
      visible: false,
      showInColumnChooser: false,
      validationRules: [],
    }),
    dataGridColumn.useDocumentDateColumn({validationRules: [{ type: 'required' }]}),
    dataGridColumn.useSeriesColumn(),
    dataGridColumn.useNumberMnemonicColumn(),
    dataGridColumn.useSaleDocumentTypeColumn({
      setCellValue: withDocumentSeriesAndNumberMnemonicUpdate(async(newData, value, currentData) => {
        newData.sale_document_type = currentSaleDocumentType.value = value
        currentData.number_mnemonic = null

        isBillingCustomerDisabled.value = !(value === portalStore.documentTypesEnum.SALE_CORRESPONDENT || value === portalStore.documentTypesEnum.SALE_DDT)
        if (isBillingCustomerDisabled.value) {
          newData.billing_customer_id = null
        }

        const isBillingSectionDisabled = !(value === portalStore.documentTypesEnum.SALE_INVOICE || value === portalStore.documentTypesEnum.SALE_IMMEDIATE_INVOICE)
        newData.billing_base = newData.billing_rate = newData.billing_reason = null
        editors['billing_base']?.option('disabled', isBillingSectionDisabled)
        editors['billing_rate']?.option('disabled', isBillingSectionDisabled)
        editors['billing_reason']?.option('disabled', isBillingSectionDisabled)

        setSdiDocumentTypeValue(currentData, newData)
        if (value === portalStore.documentTypesEnum.SALE_CREDIT_NOTE) {
          newData.sdi_document_type = portalStore.getLocalizedElectronicInvoiceSdiDocumentTypes().find(i => i.code === portalStore.electronicInvoiceSdiDocumentTypeEnum.CREDIT_NOTE)?.code
        }

        updateTabPanelVisibility()
      }),
      validationRules: [{ type: 'required' }],
      currentSaleDocumentPage: currentSaleDocumentPage
    }),
    dataGridColumn.useSaleReasonRefColumn({
      setCellValue: async(newData, value, currentData) => {
        newData.sale_reason_ref = value
        if (currentData.sale_document_type !== portalStore.documentTypesEnum.SALE_CREDIT_NOTE) {
          setSdiDocumentTypeValue({ sale_reason_ref: value }, newData)
        }
      },
    }),
    dataGridColumn.useDepositColumn({
      dataField: 'deposit',
      visible: false,
      showInColumnChooser: false,
      setCellValue: withDocumentSeriesAndNumberMnemonicUpdate((newData, value) => { newData.deposit = value?.id }),
    }),
    {
      dataField: 'sale_department',
      caption: i18n.t('common.saleDepartment'),
      lookup: useLookup({ store: portalStore.getLocalizedSaleDepartments() }),
      setCellValue: withDocumentSeriesAndNumberMnemonicUpdate((newData, value) => {
        newData.sale_department = value
      }),
    },
    dataGridColumn.useReferenceDocumentColumn({
      dataField: 'source_sale_document',
      caption: i18n.t('common.generatingDocumentRef'),
      editCellTemplate: 'generatingDocumentRef',
      cellTemplate: 'generatingDocumentRef',
      allowFiltering: false
    }),
    dataGridColumn.useReferenceDocumentColumn({
      dataField: 'generated_document',
      caption: i18n.t('common.generatedDocumentRef'),
      editCellTemplate: 'generatedDocumentRef',
      cellTemplate: 'generatedDocumentRef',
      allowFiltering: false
    }),
    {
      dataField: 'preparatory_user',
      caption: i18n.t('common.userPreparation'),
      editCellTemplate: 'mechanicsEditor',
      cellTemplate: 'mechanicsAvatarList',
      lookup: useLookup({
        store: webSiteStore.userDetails,
        sort: 'name',
        displayExpr: (item) => `${item.name} ${item.name_ext}`,
      }),
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.usePaymentTypeColumn({visible: false, showInColumnChooser: false, validationRules: [{ type: 'required' }]}),
    {
      caption: i18n.t('common.urgency'),
      dataField: 'urgency',
      dataType: 'boolean',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useDiscountColumn({visible: false, showInColumnChooser: false}),
    {
      caption: i18n.t('common.chargePercentageAlt'),
      dataField: 'charge_percentage',
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.paid'),
      dataField: 'paid',
      dataType: 'boolean',
      setCellValue: (newData, value, currentData) => {
        newData.paid = value
        isPaid.value = value
      },
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useCurrencyColumn({ editors, visible: false, showInColumnChooser: false }),
    dataGridColumn.useExchangeRateColumn({visible: false, showInColumnChooser: false}),
    dataGridColumn.useExchangeRateDateColumn({visible: false, showInColumnChooser: false}),
    dataGridColumn.useCustomerColumn({
      caption: i18n.t('common.customerData'),
      dataField: 'billing_customer_id',
      editCellTemplate: 'billingCustomerEditor',
      setCellValue: async(newData, value, currentData) => {
        newData.billing_customer = value?.id || null
        newData.billing_customer_id = value?.id
        setIncludesVat(newData, value, currentData, includesVat, currentData.customer)
      },
      visible: false,
      showInColumnChooser: false,
      validationRules: [{
        type: 'custom',
        message: i18n.t('error.validation.billingCustomerEqualsCustomer'),
        reevaluate: true,
        validationCallback: (e) => {
          if (!e.value) return true
          return e.value !== e.data.customer_id
        }
      }],
    }),
    dataGridColumn.useCustomerColumn({
      caption: i18n.t('common.customerData'),
      dataField: 'customer_id',
      editCellTemplate: 'customerEditor',
      setCellValue: async(newData, value, currentData) => {
        dataGridColumn.useCustomerColumn().setCellValue(newData, value)
        newData.customer = value || { ...webSiteStore.emptyCustomer }
        newData.customer_id = newData.customer?.id

        const customer = (await registryStore.getOrFetchCustomersAsync()).find(c => c.id === newData.customer?.id) ?? null
        const currentBillingCustomer = registryStore.customers.find(c => c.id === customer?.invoice_to_customer) ?? null

        if (!isBillingCustomerDisabled.value && (currentData.billing_customer_id === null || currentData.billing_customer_id === undefined)) {
          newData.billing_customer_id = currentBillingCustomer?.id ?? null
        }

        if (currentData.rows_count > 0) {
          newData.customer.vat_type_ref = null
        }
        if (!currentData.rows_count) {
          newData.vat_type_ref = newData.customer.vat_type_ref
        }
        if (currentData.rows_count === 0) {
          newData.currency_ref = customer?.accounting_currency_ref || webSiteStore.getDefaultCurrency()?.id
        }

        setIncludesVat(newData, value, currentData, includesVat, customer)
        if (value) {
          setSubjectType(newData.customer, currentData, value.subject_type_ref, value.subject_sub_type_ref)
        }

        if (customer?.accounting_payment_type_ref) {
          newData.payment_type_ref = customer.accounting_payment_type_ref
        }

        currentCustomer.value = newData.customer

        if (newData.customer_id !== currentData.customer_id) {
          newData.delivery_address = { ...webSiteStore.emptyDeliveryAddress }
          newData.delivery_address_ref = null

          currentCountryId.value = newData.customer?.address?.country
          currentStateId.value = newData.customer?.address?.state
          currentDeliveryCountryId.value = null
        } else {
          newData.delivery_address = currentData.delivery_address
          newData.delivery_address_ref = currentData.delivery_address_ref
        }

        newData.stamp_paid_by_customer = !!value?.stamp_by_customer

        gridRef.value.instance.option('editing.form.items', getEditColumns(newData.customer?.subject_sub_type_ref, currentSaleDocumentPage.value))

        setReadOnlyDeliveryAddress(newData)
      },
    }),
    dataGridColumn.useShippingCostTypeColumn({visible: false, showInColumnChooser: false}),
    dataGridColumn.useCustomerColumn({
      caption: i18n.t('common.carrier'),
      dataField: 'carrier_ref',
      editCellTemplate: 'carrierEditor',
      visible: false,
      showInColumnChooser: false,
      validationRules: [{
        type: 'custom',
        reevaluate: true,
        validationCallback: (e) => {
          if (e.data?.transport_type === portalStore.transportTypeEnum.COURIER) { return !!e.value }

          return true
        },
      }],
    }),
    dataGridColumn.useVatTypeColumn({ dataField: 'vat_type_ref', caption: i18n.t('common.accountingVat'), visible: false, showInColumnChooser: false, validationRules: [] }),
    {
      dataField: 'customer.subject_type_ref',
      caption: i18n.t('common.personType'),
      dataType: 'number',
      lookup: useLookup({ dataSource: getSubjectTypes }),
      setCellValue: (newData, value, currentData) => {
        newData.customer = currentData.customer || {}
        currentCustomerTypeId.value = value
        currentCustomerSubTypeId.value = null
        setSubjectType(newData.customer, currentData, value)
        editors['customer.vat_number'].option('disabled', !isVatCountryIta(currentData.customer?.vat_country_id))
      },
      validationRules: [{ type: 'required' }],
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'customer.subject_sub_type_ref',
      caption: `${i18n.t('common.category')} / ${i18n.t('common.gender')}`,
      dataType: 'number',
      lookup: useLookup({ dataSource: getSubjectSubTypes }),
      setCellValue: (newData, value, currentData) => {
        newData.customer = currentData.customer || {}
        currentCustomerSubTypeId.value = value
        setSubjectType(newData.customer, currentData, currentData.customer?.subject_type_ref, value)
      },
      validationRules: [{ type: 'required' }],
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.tag', 2),
      dataField: 'tags',
      editCellTemplate: 'tagsEditor',
      cellTemplate: 'tagsEditor',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useCustomerAddressCountryColumn({ setCellValue: (newData, value, currentData) => dataGridColumn.useAddressRef().setCellValueCountry(newData, value, currentData) }),
    dataGridColumn.useCustomerAddressStateColumn({ setCellValue: (newData, value, currentData) => dataGridColumn.useAddressRef().setCellValueState(newData, value, currentData) }),
    dataGridColumn.useCustomerAddressCityColumn({ setCellValue: (newData, value, currentData) => setCellValueCity(newData, value, currentData) }),
    { dataField: 'customer.code', visible: false, showInColumnChooser: false, validationRules: [{ type: 'required' }], caption: `${i18n.t('common.customer')} ${i18n.t('common.code')}`},
    { dataField: 'customer.name', visible: false, showInColumnChooser: false, validationRules: [{ type: 'required' }], caption: `${i18n.t('common.customer')} ${i18n.t('common.nameAlt')}`},
    {
      dataField: 'customer.name_ext',
      visible: false,
      showInColumnChooser: false,
      caption: `${i18n.t('common.customer')} ${i18n.t('common.nameExt')}`,
      validationRules: [{
        type: 'custom',
        reevaluate: true,
        message: i18n.t('common.required'),
        validationCallback: (e) => !isNameExtMandatory(e.data?.customer?.subject_type_ref) || !!e.value,
      }],
    },
    {
      caption: `${i18n.t('common.customer')} ${i18n.t('common.vatCountry')}`,
      dataField: 'customer.vat_country_id',
      editCellTemplate: 'vatCountryEditor',
      setCellValue: (newData, value, currentData) => {
        newData.customer = currentData.customer || {}
        newData.customer.vat_country_id = value?.id
        editors['customer.vat_number'].option('disabled', !isVatCountryIta(newData.customer?.vat_country_id))
      },
      visible: false,
      showInColumnChooser: false,
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            const subjectTypeCode = registryStore.subjectTypes.find(i => i.id === e.data?.customer?.subject_type_ref)?.code
            if (subjectTypeCode === 'E') return !!e.value

            return true
          }
        }
      ],
    },
    dataGridColumn.useCustomerAddressStreetColumn(),
    dataGridColumn.useCustomerVatNumberColumn(),
    dataGridColumn.useTaxCodeColumn(
      {
        dataField: 'customer.tax_code',
        caption: `${i18n.t('common.customer')} ${i18n.t('common.taxCode')}`,
        visible: false,
        showInColumnChooser: false,
        validationRules: [
          {
            type: 'custom',
            reevaluate: true,
            validationCallback: (e) => !registryStore.companyData.subject_validate_vat_and_fiscal_code || validateTaxCode(e.value, currentCustomerVatCountryCode.value),
          },
          {
            type: 'custom',
            message: i18n.t('common.required'),
            reevaluate: true,
            validationCallback: (e) => {
              const isRelevantDocType = [portalStore.documentTypesEnum.SALE_INVOICE, portalStore.documentTypesEnum.SALE_IMMEDIATE_INVOICE, portalStore.documentTypesEnum.SALE_CREDIT_NOTE, portalStore.documentTypesEnum.SALE_DDT].includes(e.data.sale_document_type)
              if (!isRelevantDocType) return true

              const subjectTypeCode = registryStore.subjectTypes.find(i => i.id === e.data?.customer?.subject_type_ref)?.code
              if (subjectTypeCode === 'P') return !!e.value

              return true
            }
          }
        ],
      }
    ),
    { dataField: 'customer.address.country_name', caption: `${i18n.t('common.customer')} ${i18n.t('common.country')}`, visible: false, showInColumnChooser: false},
    { dataField: 'customer.address.state_name', caption: `${i18n.t('common.customer')} ${i18n.t('common.state')}`, visible: false, showInColumnChooser: false},
    { dataField: 'customer.address.city_name', caption: `${i18n.t('common.customer')} ${i18n.t('common.city')}`, visible: false, showInColumnChooser: false},
    dataGridColumn.useCustomerAddressZipCodeColumn({setCellValueZipCode: (newData, value, currentData, type) => setCellValueZipCode(newData, value, currentData, 'customer', false)}),
    dataGridColumn.useEmailColumn({ dataField: 'customer.email', caption: `${i18n.t('common.customer')} ${i18n.t('common.email')}`}),
    dataGridColumn.useCustomerMobileColumn(),
    { dataField: 'customer.phone', caption: `${i18n.t('common.customer')} ${i18n.t('common.phone')}`, visible: false, showInColumnChooser: false},
    dataGridColumn.useDeliveryAddressColumn({
      caption: i18n.t('common.deliveryAddress'),
      dataField: 'delivery_address_ref',
      editCellTemplate: 'deliveryAddressEditor',
      setCellValue: async(newData, value, currentData) => {
        await registryStore.getOrFetchSubjectContactsAsync()
        dataGridColumn.useDeliveryAddressColumn().setCellValue(newData, value)
        newData.delivery_address = value || { ...webSiteStore.emptyDeliveryAddress }
        newData.delivery_address.id = newData.delivery_address_ref = Math.abs(newData.delivery_address?.id)
        currentDeliveryCountryId.value = newData.delivery_address?.address?.country
        currentDeliveryStateId.value = newData.delivery_address?.address?.state
        if (!!currentData.delivery_address_ref && !value) {
          newData.delivery_address = { ...webSiteStore.emptyDeliveryAddress }
          currentData.delivery_address = { ...webSiteStore.emptyDeliveryAddress }
          delete newData.delivery_address.id
        }
      },
      onlyCustomer: true,
      validationRules: [{
        type: 'custom',
        message: i18n.t('common.required'),
        reevaluate: true,
        validationCallback: (e) => {
          return !hasAnyValue(e.data?.delivery_address) || !!e.value
        }
      }],
    }),
    {
      dataField: 'delivery_address.name',
      visible: false,
      showInColumnChooser: false,
      caption: `${i18n.t('common.customer')} ${i18n.t('common.nameAlt')}`,
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            return !hasAnyValue(e.data?.delivery_address) || !!e.value
          }
        }
      ],
    },
    { dataField: 'delivery_address.email', caption: `${i18n.t('common.customer')} ${i18n.t('common.email')}`, visible: false, showInColumnChooser: false},
    { dataField: 'delivery_address.phone', caption: `${i18n.t('common.phone')} ${i18n.t('common.phone')}`, visible: false, showInColumnChooser: false},
    { dataField: 'delivery_address.mobile', caption: `${i18n.t('common.mobile')} ${i18n.t('common.mobile')}`, visible: false, showInColumnChooser: false},
    dataGridColumn.useCustomerAddressCountryColumn({
      dataField: 'delivery_address.address.country',
      editCellTemplate: 'deliveryAddressCountryEditor',
      field: 'delivery_address',
      setCellValue: (newData, value, currentData) => dataGridColumn.useAddressRef().setCellValueCountry(newData, value, currentData, 'delivery_address'),
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            return !hasAnyValue(e.data?.delivery_address) || !!e.value
          }
        }
      ],
    }),
    dataGridColumn.useCustomerAddressStateColumn({
      dataField: 'delivery_address.address.state',
      field: 'delivery_address',
      editCellTemplate: 'deliveryAddressStateEditor',
      setCellValue: (newData, value, currentData) => dataGridColumn.useAddressRef().setCellValueState(newData, value, currentData, 'delivery_address'),
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            const address = e.data?.delivery_address?.address
            return !hasAnyValue(e.data?.delivery_address) || !!address?.state_text || !!e.value
          }
        }
      ],
    }),
    dataGridColumn.useCustomerAddressCityColumn({
      dataField: 'delivery_address.address.city',
      editCellTemplate: 'deliveryAddressCityEditor',
      field: 'delivery_address',
      setCellValue: (newData, value, currentData) => setCellValueCity(newData, value, currentData, 'delivery_address'),
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            const address = e.data?.delivery_address?.address
            return !hasAnyValue(e.data?.delivery_address) || !!address?.city_text || !!e.value
          }
        }
      ],
    }),
    dataGridColumn.useCustomerAddressStreetColumn({
      dataField: 'delivery_address.address.street',
      field: 'delivery_address',
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            return !hasAnyValue(e.data?.delivery_address) || !!e.value
          }
        }
      ],
    }),
    { dataField: 'delivery_address.address.country_name',
      caption: `${i18n.t('common.customer')} ${i18n.t('common.country')}`,
      visible: false,
      showInColumnChooser: false,
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            return !hasAnyValue(e.data?.delivery_address) || !!e.value
          }
        }
      ],
    },
    { dataField: 'delivery_address.address.state_name',
      caption: `${i18n.t('common.customer')} ${i18n.t('common.state')}`,
      visible: false,
      showInColumnChooser: false,
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            const address = e.data?.delivery_address?.address
            return !hasAnyValue(e.data?.delivery_address) || !!address?.state || !!e.value
          }
        }
      ],
    },
    { dataField: 'delivery_address.address.city_name',
      caption: `${i18n.t('common.customer')} ${i18n.t('common.city')}`,
      visible: false,
      showInColumnChooser: false,
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            const address = e.data?.delivery_address?.address
            return !hasAnyValue(e.data?.delivery_address) || !!address?.city || !!e.value
          }
        }
      ],
    },
    dataGridColumn.useCustomerAddressZipCodeColumn({
      dataField: 'delivery_address.address.zip_code',
      field: 'delivery_address',
      setCellValueZipCode: (newData, value, currentData) => setCellValueZipCode(newData, value, currentData, 'delivery_address', false),
      validationRules: [
        {
          type: 'custom',
          message: i18n.t('common.required'),
          reevaluate: true,
          validationCallback: (e) => {
            return !hasAnyValue(e.data?.delivery_address) || !!e.value
          }
        }
      ],
    }),
    {
      caption: i18n.t('common.number'),
      dataField: 'document_number',
      visible: false,
      showInColumnChooser: false
    },
    {
      caption: i18n.t('common.date'),
      dataField: 'document_date',
      dataType: 'date',
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'document_type',
      caption: i18n.t('common.type'),
      lookup: useLookup({ dataSource: registryStore.getLocalizedDocumentTypes() }),
      visible: false,
      showInColumnChooser: false,
      editorOptions: {
        showClearButton: true,
      },
    },
    dataGridColumn.useVehicleColumn({
      caption: i18n.t('common.vehicle'),
      dataField: 'vehicle_id',
      editCellTemplate: 'vehicleEditor',
      setCellValue: async(newData, value, currentData) => {
        dataGridColumn.useVehicleColumn().setCellValue(newData, value)
        if (value) {
          newData.vehicle = (await registryStore.getOrFetchVehiclesAsync()).find(i => i.id === value.id)
        } else {
          newData.vehicle = { ...webSiteStore.emptyVehicle }
        }
        newData.vehicle_id = newData.vehicle?.id

        if (newData.vehicle?.id) {
          const res = await registryStore.api.getVehicleNextCarServiceAsync(newData.vehicle.id)
          currentCarService.value = res.data

          if (currentData.intervention_type_ref === garageStore.getLocalizedInterventionTypes().find(el => el.code === webSiteStore.interventionTypeCodeEnum.CAR_SERVICE)?.id) {
            newData.car_service = res.data
          }
        }

        currentVehicleFuelType.value = newData.vehicle?.fuel_type ?? null
        formRepaintTabHandler()
      },
      validationRules: []
    }),
    dataGridColumn.useSupplierBrandColumn({dataField: 'vehicle.supplier_brand_ref', all: true, visible: false, showInColumnChooser: false, validationRules: [] }),
    { dataField: 'vehicle.name', caption: i18n.t('common.description'), visible: false, showInColumnChooser: false },
    {
      dataField: 'vehicle.color',
      caption: i18n.t('common.color'),
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.usePlateNumberColumn({
      validationRules: [
        {
          type: 'custom',
          reevaluate: true,
          validationCallback: validatePlateNumberNoSpaces,
          message: i18n.t('error.validation.noBlankSpacesAllowed'),
        }
      ],
      hideInfoTooltip: true
    }),
    dataGridColumn.useVinColumn({ dataField: 'vehicle.vin', visible: false, validationRules: [], hideInfoTooltip: true }),
    dataGridColumn.useEngineSizeColumn({ dataField: 'vehicle.engine_size', validationRules: [] }),
    {
      caption: i18n.t('common.engineCode'),
      dataField: 'vehicle.engine_code',
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.km'),
      dataField: 'vehicle.km',
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useTotalNoVatColumn(),
    dataGridColumn.useTotalVatColumn(),
    dataGridColumn.useNotesColumn({visible: false, showInColumnChooser: false}),
    {
      dataField: 'transport_date',
      caption: i18n.t('common.date'),
      dataType: 'datetime',
      visible: false,
      showInColumnChooser: false,
      calculateDisplayValue: (rowData) => rowData.transport_date ? moment(rowData.transport_date).local().toDate() : null,
      calculateSortValue: 'transport_date',
      setCellValue: (newData, value) => {
        newData.transport_date = value ? moment(value).toISOString() : null
      },
      validationRules: [{
        type: 'custom',
        reevaluate: true,
        message: i18n.t('common.required'),
        validationCallback: (e) => {
          const isRequired = currentSaleDocumentType.value & (currentSaleDocumentType.value === portalStore.documentTypesEnum.SALE_DDT || currentSaleDocumentType.value === portalStore.documentTypesEnum.SALE_IMMEDIATE_INVOICE)
          if (!isRequired) { return true }

          return !!e.value
        }
      }],
    },
    {
      dataField: 'transport_package_num',
      caption: i18n.t('common.packageNum'),
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'transport_weight',
      caption: i18n.t('common.weight'),
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'transport_type',
      caption: i18n.t('common.transportType'),
      lookup: useLookup({ dataSource: portalStore.getLocalizedTransportTypes() }),
      visible: false,
      showInColumnChooser: false,
    },
    {
      dataField: 'packaging_type_ref',
      caption: i18n.t('common.packagingCode'),
      lookup: useLookup({ dataSource: registryStore.getLocalizedPackagingTypes() }),
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.taxableBase'),
      dataField: 'billing_base',
      dataType: 'number',
      format: { precision: 2 },
      setCellValue: (newData, value, currentData) => {
        newData.billing_base = value
        newData.billing_amount = (value || 0) * (currentData.billing_rate || 0)
      },
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.percentage'),
      dataField: 'billing_rate',
      dataType: 'number',
      setCellValue: (newData, value, currentData) => {
        newData.billing_rate = value
        newData.billing_amount = (currentData.billing_base || 0) * (value || 0)
      },
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.amount'),
      dataField: 'billing_amount',
      dataType: 'number',
      format: { precision: 2 },
      visible: false,
      showInColumnChooser: false,
      allowEditing: false,
    },
    {
      caption: i18n.t('common.reason'),
      dataField: 'billing_reason',
      setCellValue: (newData, value) => {
        newData.billing_reason = value
      },
      visible: false,
      showInColumnChooser: false,
      validationRules: [{
        type: 'custom',
        reevaluate: true,
        message: i18n.t('common.required'),
        validationCallback: (e) => (e.data.billing_amount ?? 0) <= 0 || !!e.value
      }],
    },
    {
      caption: i18n.t('common.stampPaidByCustomer'),
      dataField: 'stamp_paid_by_customer',
      dataType: 'boolean',
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.priceVat', 2),
      dataField: 'includes_vat',
      dataType: 'boolean',
      visible: false,
      showInColumnChooser: false,
      setCellValue: (newData, value) => {
        newData.includes_vat = value
        includesVat.value = value
      }
    },
    {
      caption: i18n.t('common.stampAmount'),
      dataField: 'stamp_amount',
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
      calculateDisplayValue: (rowData) => useCurrencyColumnFormatter(rowData, 'stamp_amount', rowData.currency_ref),
      calculateSortValue: 'stamp_amount',
    },
    dataGridColumn.useVatTypeColumn({
      dataField: 'stamp_vat_type_ref',
      caption: i18n.t('common.vatExemption'),
      visible: false,
      showInColumnChooser: false,
      exemption_vat_type: true,
      setCellValue: (newData, value) => {
        newData.stamp_vat_type_ref = value
        if (value == null) {
          newData.stamp_amount = null
        }
      },
      validationRules: [{
        type: 'custom',
        reevaluate: true,
        message: i18n.t('common.required'),
        validationCallback: (e) => {
          if (!e.data?.stamp_amount || e.data?.stamp_amount <= 0) return true
          return !!e.value
        },
      }],
    }),
    {
      caption: i18n.t('common.conventionType'),
      dataField: 'sdi_convention_type',
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.authorizationType'),
      dataField: 'sdi_authorization_type',
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.purchaseOrderNumber'),
      dataField: 'sdi_purchase_order_number',
      visible: false,
      showInColumnChooser: false,
      validationRules: [{
        type: 'custom',
        message: i18n.t('common.required'),
        reevaluate: true,
        validationCallback: (e) => {
          if (e.data?.sdi_cig || e.data?.sdi_cup) {
            return !!e.value
          }

          return true
        }
      }],
    },
    {
      caption: i18n.t('common.purchaseOrderLine'),
      dataField: 'sdi_purchase_order_line',
      visible: false,
      showInColumnChooser: false,
    },
    dataGridColumn.useDateColumn({ caption: i18n.t('common.orderDate'), dataField: 'sdi_order_date' }),
    {
      caption: i18n.t('common.CIG'),
      dataField: 'sdi_cig',
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.CUP'),
      dataField: 'sdi_cup',
      visible: false,
      showInColumnChooser: false,
    },
    {
      caption: i18n.t('common.electronicInvoiceSdiDocumentType'),
      dataField: 'sdi_document_type',
      visible: false,
      showInColumnChooser: false,
    },
  ]
}
